import { useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserButton } from "@clerk/clerk-react";
import {
  LayoutDashboard, Trophy, Sprout, Wrench, Truck, FileText,
  ScrollText, Settings as SettingsIcon, Users, Send, LogOut, Activity,
  MessageSquare, Heart, ShieldCheck, Menu, DollarSign, CalendarRange, Sparkles, Wallet,
  Plug, ClipboardList, ChevronLeft, ChevronRight, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { WeatherChip, WeatherChipMobile } from "@/components/WeatherChip";
import { DateTimeChip, DateTimeChipMobile } from "@/components/DateTimeChip";
import { QuickEditFAB } from "@/components/QuickEditFAB";
import { WeeklyWrapController } from "@/components/WeeklyWrapController";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LogoLockup } from "@/components/Logo";
import { BaseSwitcher } from "@/components/BaseSwitcher";
import { useBase } from "@/contexts/BaseContext";
import { BackgroundMediaLayer } from "@/components/BackgroundMediaLayer";
import { QUICK_EDIT_EVENT } from "@/hooks/useQuickEditRefresh";
import { resolveHomePath } from "@/lib/home-path";

// `perm`     → hides the item if the user lacks the permission key (granular RBAC).
// `adminOnly`→ legacy gate that still hides items from non-admins (WhatsApp / Settings / Audit).
// `masterOnly` → only the master admin sees Members & Roles.
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard:read" },
  { to: "/horses", label: "Horses", icon: Activity, perm: "horses:read" },
  { to: "/horses/rankings", label: "Rankings", icon: Trophy, perm: "rankings:read" },
  { to: "/assistant", label: "Assistant", icon: Sparkles, perm: "assistant:use" },
  { to: "/connector", label: "ChatGPT Connector", icon: Plug, perm: "assistant:use" },
  { to: "/tasks", label: "Tasks", icon: ClipboardList, perm: "dashboard:read" },
  { to: "/seasons", label: "Seasons", icon: CalendarRange, perm: "trends:read" },
  { to: "/breeding", label: "Breeding", icon: Heart, perm: "breeding:read" },
  { to: "/staff", label: "Staff", icon: Users, perm: "staff:read" },
  { to: "/inventory/feed", label: "Feed", icon: Sprout, perm: "inventory:read" },
  { to: "/inventory/tack", label: "Tack", icon: Wrench, perm: "inventory:read" },
  { to: "/vehicles", label: "Vehicles", icon: Truck, perm: "inventory:read" },
  { to: "/invoices", label: "Invoices", icon: FileText, perm: "invoices:read" },
  { to: "/salaries", label: "Salaries", icon: Wallet, perm: "salaries:read" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageSquare, adminOnly: true },
  { to: "/whatsapp-editor", label: "WhatsApp Editor", icon: Send, adminOnly: true },
  { to: "/audit", label: "Audit", icon: ScrollText, adminOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
  { to: "/members", label: "Members & Roles", icon: ShieldCheck, masterOnly: true },
  { to: "/locations", label: "Bases & Locations", icon: MapPin, perm: "locations:manage" },
  { to: "/costs", label: "Tech costs", icon: DollarSign, perm: "costs:read" },
];

export default function AppShell() {
  const { user, logout, isAdmin, isMaster, hasPerm } = useAuth();
  const { showSwitcher, allAccess } = useBase();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop sidebar collapse (persisted). When hidden, a brand "reveal"
  // pill (AP logo + arrow) floats at the top-left to bring it back.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("ap-sidebar-collapsed") === "1"; } catch (_e) { return false; }
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    const n = !c;
    try { localStorage.setItem("ap-sidebar-collapsed", n ? "1" : "0"); } catch (_e) {}
    return n;
  });

  const navItems = NAV.filter((n) => {
    if (n.masterOnly) return isMaster;
    if (n.adminOnly) return isAdmin;
    if (n.perm) return hasPerm(n.perm);
    return true;
  });

  // Resolved home path for this user. The African Polistas logo (both
  // sidebar + mobile header) links here so restricted users land on
  // their first permitted page instead of the "/" dashboard which they
  // may not have access to. Falls back to "/" if no accessible page is
  // found — the HomeRoute in App.js will then render the No Access
  // panel instead of looping.
  const homePath = resolveHomePath({ hasPerm, isAdmin, isMaster }) || "/";

  const activeItem = navItems.find((n) =>
    location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to))
  );

  // A stable page key for the background media layer. We keep it
  // deterministic (based on the top-level route) rather than deeply
  // pathname-derived so a horse-detail page picks up the same footage
  // as the horses list. "global" is the default for anything unmapped.
  const pageKey = (() => {
    const p = location.pathname;
    if (p === "/" || p.startsWith("/dashboard")) return "dashboard";
    if (p.startsWith("/horses/rankings")) return "rankings";
    if (p.startsWith("/horses")) return "horses";
    if (p.startsWith("/breeding")) return "breeding";
    if (p.startsWith("/staff")) return "staff";
    if (p.startsWith("/inventory")) return "inventory";
    if (p.startsWith("/vehicles")) return "vehicles";
    if (p.startsWith("/invoices")) return "invoices";
    if (p.startsWith("/whatsapp")) return "whatsapp";
    if (p.startsWith("/audit")) return "audit";
    if (p.startsWith("/settings")) return "settings";
    if (p.startsWith("/members")) return "members";
    return "global";
  })();

  return (
    <div className="ap-shell-root min-h-screen flex text-foreground">
      {/* Fixed-viewport background media layer + scrim behind everything.
          Watermark placeholder renders when no media is assigned; Phase D
          replaces that with real footage. */}
      <BackgroundMediaLayer pageKey={pageKey} />

      {/* Persistent weather chip. top-right corner of every page (desktop only) */}
      <WeatherChip />
      {/* Persistent live SAST date/time chip. top-LEFT (mirror position
          of the weather chip). Sits just to the right of the floating
          sidebar so both anchors are visible on every page. */}
      <DateTimeChip />

      {/* Desktop sidebar — floating panel inset from viewport edges.
          A sibling spacer div reserves horizontal space in the flex
          layout so the fixed-position sidebar doesn't overlap main
          content. This gives the user the requested "floating" look
          (rounded corners, drop shadow, margin around it) while
          preserving the two-column reading flow. */}
      <div
        className="hidden lg:block shrink-0 transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ width: collapsed ? 0 : 264 }}
        aria-hidden="true"
      />
      <aside
        className={cn(
          "ap-sidebar hidden lg:flex w-[240px] flex-col fixed top-4 left-4 bottom-4 z-20 overflow-hidden",
          "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform",
          collapsed
            ? "-translate-x-[118%] opacity-0 pointer-events-none"
            : "translate-x-0 opacity-100"
        )}
        data-testid="desktop-sidebar"
      >
        <div className="px-6 py-6 flex items-center justify-between gap-2">
          <Link to={homePath} className="flex items-center" data-testid="sidebar-logo-link">
            <LogoLockup size={44} />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleCollapsed}
            data-testid="sidebar-collapse-btn"
            aria-label="Hide sidebar"
            title="Hide sidebar"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        {showSwitcher && (
          <div className="px-3 pb-3" data-testid="sidebar-base-switcher">
            <BaseSwitcher variant="desktop" />
          </div>
        )}
        <nav className="flex-1 px-3 pb-4 space-y-1 overflow-y-auto">
          {allAccess && (
            <Link
              to="/bases/overview"
              data-testid="sidebar-nav-all-bases-link"
              className={cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                location.pathname === "/bases/overview" && "bg-accent text-foreground shadow-[inset_3px_0_0_hsl(var(--ap-forest))]"
              )}
            >
              <MapPin className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
              <span>All Bases</span>
            </Link>
          )}
          {navItems.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={cn(
                  "group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground/80 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  active && "bg-accent text-foreground shadow-[inset_3px_0_0_hsl(var(--ap-forest))]"
                )}
              >
                <Icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t">
          <div className="flex items-center gap-3">
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "h-9 w-9 rounded-full ring-1 ring-border",
                  userButtonPopoverCard: "shadow-lg",
                },
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name || ""}</div>
              <div className="truncate text-xs text-muted-foreground">{user?.username || user?.email}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="sidebar-logout-button" title="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            {user?.is_master ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.1)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--ap-saddle))]">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--ap-brass))]" />
                Master admin
              </div>
            ) : <div />}
            <ThemeToggle data-testid="sidebar-theme-toggle" />
          </div>
        </div>
      </aside>

      {/* Desktop "reveal" pill — the African Polistas logo IS the unhide
          control. Fades/slides in only when the sidebar is collapsed. */}
      <button
        type="button"
        onClick={toggleCollapsed}
        data-testid="sidebar-reveal-btn"
        aria-label="Show sidebar"
        title="Show sidebar"
        className={cn(
          "hidden lg:flex items-center gap-1.5 fixed top-4 left-4 z-30 rounded-full py-1.5 pl-1.5 pr-3",
          "bg-[hsl(var(--ap-forest))] text-white shadow-lg ring-1 ring-black/10",
          "transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform",
          "hover:bg-[hsl(var(--ap-forest))]/90 hover:pr-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed
            ? "opacity-100 translate-x-0 scale-100"
            : "opacity-0 -translate-x-6 scale-90 pointer-events-none"
        )}
      >
        <img
          src="/brand/logo-white.png"
          alt="African Polistas"
          className="h-8 w-8 object-contain shrink-0"
          draggable={false}
        />
        <ChevronRight className="h-4 w-4 shrink-0" strokeWidth={2.5} />
      </button>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 border-b bg-background/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-2">
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-trigger"
              aria-label="Open navigation menu"
              className="-ml-1"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="ap-sidebar w-[280px] p-0 flex flex-col" data-testid="mobile-nav-drawer">
            <SheetHeader className="px-5 py-4 border-b border-[color:var(--border-hairline)] text-left">
              <SheetTitle asChild>
                <LogoLockup size={36} />
              </SheetTitle>
            </SheetHeader>
            <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto" data-testid="mobile-nav-items">
              {showSwitcher && (
                <div className="px-1 pb-2" data-testid="mobile-base-switcher">
                  <BaseSwitcher variant="desktop" />
                </div>
              )}
              {allAccess && (
                <Link
                  to="/bases/overview"
                  onClick={() => setDrawerOpen(false)}
                  data-testid="mobile-nav-all-bases-link"
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-3 text-[15px] text-foreground/85 hover:bg-accent hover:text-foreground active:bg-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    location.pathname === "/bases/overview" && "bg-accent text-foreground shadow-[inset_3px_0_0_hsl(var(--ap-forest))]"
                  )}
                >
                  <MapPin className="h-[18px] w-[18px] text-muted-foreground" />
                  <span>All Bases</span>
                </Link>
              )}
              {navItems.map(({ to, label, icon: Icon }) => {
                const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setDrawerOpen(false)}
                    data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-3 text-[15px] text-foreground/85 hover:bg-accent hover:text-foreground active:bg-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active && "bg-accent text-foreground shadow-[inset_3px_0_0_hsl(var(--ap-forest))]"
                    )}
                  >
                    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
                    <span>{label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="border-t px-4 py-4">
              <div className="flex items-center gap-3">
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: "h-9 w-9 rounded-full ring-1 ring-border",
                      userButtonPopoverCard: "shadow-lg",
                    },
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium" data-testid="mobile-user-name">{user?.name || ""}</div>
                  <div className="truncate text-xs text-muted-foreground">{user?.username || user?.email}</div>
                </div>
              </div>
              {/* Explicit sign-out button on mobile. The Clerk <UserButton>
                  popover works but its tap target is small and the popover
                  itself gets clipped inside the drawer sheet on smaller
                  phones — users reported the sign-out link being hard to
                  reach. Big, obvious button here fixes that. */}
              <Button
                variant="outline"
                className="mt-3 w-full h-11 justify-center text-sm"
                onClick={() => { setDrawerOpen(false); logout(); }}
                data-testid="mobile-logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
              <div className="mt-3 flex items-center justify-between gap-2">
                {user?.is_master ? (
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ap-brass)/0.4)] bg-[hsl(var(--ap-brass)/0.1)] px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--ap-saddle))]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--ap-brass))]" />
                    Master admin
                  </div>
                ) : <div />}
                <ThemeToggle data-testid="mobile-theme-toggle" />
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Centre. current section label (replaces the brand line so users
            know where they are without crowding the bar). */}
        <Link to={homePath} className="flex items-center gap-2 min-w-0 flex-1" data-testid="mobile-header-title">
          <img src="/brand/logo-white.png" alt="" className="h-7 w-7 object-contain shrink-0 dark:block hidden" draggable={false} />
          <img src="/brand/logo-dark.png"  alt="" className="h-7 w-7 object-contain shrink-0 dark:hidden block" draggable={false} />
          <span className="font-serif text-base truncate">
            {activeItem?.label || "African Polistas"}
          </span>
        </Link>

        <div className="flex items-center gap-1.5 shrink-0">
          <DateTimeChipMobile />
          <WeatherChipMobile />
        </div>
      </div>

      {/* Main */}
      <main className="ap-shell-main flex-1 min-w-0 pt-14 lg:pt-0 pb-6 lg:pb-0">
        <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>

      {/* Global Quick Edit. available on every protected page for users
          with the `horses:quick_edit` permission. Master admins always
          see it. Pages refresh themselves by listening for
          QUICK_EDIT_EVENT. */}
      {(isMaster || hasPerm?.("horses:quick_edit")) && (
        <QuickEditFAB
          onApplied={() => window.dispatchEvent(new CustomEvent(QUICK_EDIT_EVENT))}
        />
      )}
      <WeeklyWrapController />
    </div>
  );
}
